import { Selection } from 'd3-selection'
import { sum } from 'd3-array'

// Types
import { TextAlign, TrimMode, UnovisText, UnovisTextFrameOptions, UnovisTextOptions, UnovisWrappedText, VerticalAlign } from '@/types/text'
import { Rect } from '@/types/misc'

// Utils
import { flatten, isArray, merge } from '@/utils/data'
import { getTextAnchorFromTextAlign } from '@/types/svg'
import { getRotatedRectAabb } from '@/utils/misc'
import { estimateStringPixelLength, getCachedComputedTextLength, getPreciseStringLengthPx } from '@/utils/text-measure'
import { toPx } from '@/utils/to-px'

// Styles
import { getFontWidthToHeightRatio, UNOVIS_TEXT_DEFAULT, UNOVIS_TEXT_SEPARATOR_DEFAULT, UNOVIS_TEXT_HYPHEN_CHARACTER_DEFAULT } from '@/styles/index'

export const textAlignToAnchor = (textAlign: TextAlign): string | null => {
  switch (textAlign) {
    case TextAlign.Left: return 'start'
    case TextAlign.Right: return 'end'
    case TextAlign.Center: return 'middle'
    default: return null
  }
}

/**
 * Converts a kebab-case string to camelCase.
 *
 * @param {string} str - The kebab-case string to be converted.
 * @returns {string} The resulting camelCase string.
 */
export function kebabCaseToCamel (str: string): string {
  return str.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())
}

/**
 * Converts a given string to kebab-case.
 * @param {string} str - The input string to be converted to kebab-case.
 * @returns {string} - The kebab-cased string.
 */
export function kebabCase (str: string): string {
  return str.match(/[A-Z]{2,}(?=[A-Z][a-z0-9]*|\b)|[A-Z]?[a-z0-9]*|[A-Z]|[0-9]+/g)
    ?.filter(Boolean)
    .map(x => x.toLowerCase())
    .join('-')
}

export function escapeStringKeepHash (str: string): string {
  return str
    .replace(/['"]/g, '&#39;') // Escapes quotes with &#39;
    // eslint-disable-next-line no-control-regex
    .replace(/\u0000/g, '\\0') // Escapes null characters
    .replace(/\n/g, '\\n') // Escapes new lines
    .replace(/\r/g, '\\r') // Escapes carriage returns
    .replace(/\v/g, '\\v') // Escapes vertical tabs
    .replace(/\t/g, '\\t') // Escapes horizontal tabs
    .replace(/\f/g, '\\f') // Escapes form feeds
}

/**
 * Trims the input string from the start, leaving only the specified maximum length.
 * @param {string} str - The input string to be trimmed.
 * @param {number} [maxLength=15] - The maximum allowed length of the trimmed string.
 * @returns {string} - The trimmed string.
 */
export function trimStringStart (str: string | undefined, maxLength = 15): string {
  if (!str) return ''
  return str.length > maxLength ? `…${str.substr(str.length - maxLength, maxLength)}` : str
}

/**
 * Trims the input string from the middle, leaving only the specified maximum length.
 * @param {string} str - The input string to be trimmed.
 * @param {number} [maxLength=15] - The maximum allowed length of the trimmed string.
 * @returns {string} - The trimmed string.
 */
export function trimStringMiddle (str: string | undefined, maxLength = 15): string {
  if (!str) return ''

  const dist = Math.floor((maxLength - 3) / 2)
  return str.length > maxLength ? `${str.substr(0, dist)}…${str.substr(-dist, dist)}` : str
}

/**
 * Trims the input string from the end, leaving only the specified maximum length.
 * @param {string} str - The input string to be trimmed.
 * @param {number} [maxLength=15] - The maximum allowed length of the trimmed string.
 * @returns {string} - The trimmed string.
 */
export function trimStringEnd (str: string | undefined, maxLength = 15): string {
  if (!str) return ''

  return str.length > maxLength ? `${str.substr(0, maxLength)}…` : str
}

/**
 * Trims the input string according to the specified trim mode.
 * @param {string} str - The input string to be trimmed.
 * @param {number} [length=15] - The maximum allowed length of the trimmed string.
 * @param {TrimMode} [type=TrimMode.Middle] - The trim mode to be applied.
 * @returns {string} - The trimmed string.
 */
export function trimString (str: string | undefined, length = 15, type = TrimMode.Middle): string {
  if (!str) return ''

  let result = trimStringEnd(str, length)
  if (type === TrimMode.Start) result = trimStringStart(str, length)
  else if (type === TrimMode.Middle) result = trimStringMiddle(str, length)
  return result
}

/**
 * Splits the input string according to the specified separators.
 * @param {string} text - The input string to be split.
 * @param {string[]} [separators=[' ']] - The array of separators to be used for splitting.
 * @returns {string[]} - The array of split words.
 */
export function splitString (text: string, separators = [' ']): string[] {
  let result = [text] as Array<string | string[]>
  for (let i = 0; i < separators.length; i++) {
    const sep = separators[i]
    result.forEach((d, index) => {
      const separated = (d as string).split(sep)
      const words = separated.map((word, j) => `${word}${j === separated.length - 1 ? '' : sep}`)
      result[index] = words
    })
    result = flatten(result)
  }

  return result as string[]
}

/**
 * Wraps an SVG text element to fit within the specified width.
 * @param {Selection<SVGTextElement, any, SVGElement, any>} textElement - The SVG text element to be wrapped.
 * @param {number} width - The maximum allowed width for the text element.
 * @param {(string | string[])} [separator=[' ', '-', '.', ',']] - The separator(s) to be used for wrapping.
 */
export function wrapSVGText (
  textElement: Selection<SVGTextElement, any, SVGElement, any>,
  width: number,
  separator: string | string[] = [' ', '-', '.', ',']
): void {
  const text = textElement.text()
  if (!text) return

  // Wrap
  const separators = (isArray(separator) ? separator : [separator]) as string[]
  const words = splitString(text, separators)
  const x = parseFloat(textElement.attr('x')) || 0

  textElement.text('')
  let tspan = textElement.append('tspan').attr('x', x)
  let tspanContent = `${words[0]}`
  tspan.text(tspanContent)

  words.forEach((word, i) => {
    if (i === 0) return

    const tspanText = `${tspanContent}${word}`
    tspan.text(tspanText)
    const tspanWidth = getCachedComputedTextLength(tspan.node())
    if (tspanWidth > width) {
      tspan.text(tspanContent.trim())

      tspan = textElement.append('tspan')
        .attr('x', x)
        .attr('dy', '1.2em')
        .text(word)

      tspanContent = word
    } else tspanContent += word
  })
}

/**
 * Trims an SVG text element based on the specified max width, trim type, and other options.
 * @param {Selection<SVGTextElement, any, SVGElement, any>} svgTextSelection - The D3 selection of the SVG text element to be trimmed.
 * @param {number} [maxWidth=50] - The maximum width of the text element.
 * @param {TrimMode} [trimType=TrimMode.Middle] - The type of trim (start, middle, or end).
 * @param {boolean} [fastMode=false] - Whether to use a fast estimation method for text length calculation.
 * @param {number} [fontSize=0] - The font size of the text.
 * @param {number} [fontWidthToHeightRatio=getFontWidthToHeightRatio()] - The font width to height ratio.
 * @returns {boolean} True if the text was trimmed, false otherwise.
 */
export function trimSVGText (
  svgTextSelection: Selection<SVGTextElement, any, SVGElement, any>,
  maxWidth = 50,
  trimType = TrimMode.Middle,
  fastMode = false,
  fontSize = toPx(window.getComputedStyle(svgTextSelection.node())?.fontSize || UNOVIS_TEXT_DEFAULT.fontSize),
  fontWidthToHeightRatio = getFontWidthToHeightRatio()
): boolean {
  const text = svgTextSelection.text() || ''
  const textLength = text.length
  if (!textLength) return false

  if (fastMode) {
    // Fast path: estimate width from a uniform per-character width. Cheap but
    // approximate — it ignores per-glyph width and the appended ellipsis, so the
    // result can slightly overflow `maxWidth`.
    const textWidth = estimateStringPixelLength(text, fontSize, fontWidthToHeightRatio)
    const tolerance = 1.1
    const maxCharacters = Math.ceil(textLength * maxWidth / (tolerance * textWidth))
    if (maxCharacters < textLength) {
      svgTextSelection.text(trimString(text, maxCharacters, trimType))
      return true
    }
    return false
  }

  // Accurate path: measure the real rendered width and shrink until the trimmed
  // text (ellipsis included) actually fits. Binary search keeps this to ~log2(n)
  // measurements, and measurement is canvas-backed so each is cheap.
  const node = svgTextSelection.node()
  if (getCachedComputedTextLength(node) <= maxWidth) return false

  let minCharacters = 0
  let maxCharacters = textLength
  let bestFitCharacters = 0
  while (minCharacters <= maxCharacters) {
    const candidateCharacters = (minCharacters + maxCharacters) >> 1
    svgTextSelection.text(trimString(text, candidateCharacters, trimType))
    if (getCachedComputedTextLength(node) <= maxWidth) {
      bestFitCharacters = candidateCharacters
      minCharacters = candidateCharacters + 1
    } else maxCharacters = candidateCharacters - 1
  }

  svgTextSelection.text(trimString(text, bestFitCharacters, trimType))
  return true
}


/**
 * Estimates the dimensions of an SVG text element.
 *
 * @export
 * @param {Selection<SVGTextElement, any, SVGElement, any>} svgTextSelection - The D3 selection of the SVG text element.
 * @param {number} fontSize - The font size.
 * @param {number} [dy=0.32] - The line height scaling factor.
 * @param {boolean} [fastMode=false] - Whether to use a fast estimation method or a more accurate one.
 * @param {number} [fontWidthToHeightRatio] - The font width-to-height ratio.
 * @returns {{width: number, height: number}} - The estimated dimensions of the text element.
 */
export function estimateTextSize (
  svgTextSelection: Selection<SVGTextElement, any, SVGElement, any>,
  fontSize: number,
  dy = 0.32,
  fastMode = false,
  fontWidthToHeightRatio?: number
): { width: number; height: number } {
  fontWidthToHeightRatio = fontWidthToHeightRatio || getFontWidthToHeightRatio()
  const tspanSelection = svgTextSelection.selectAll('tspan')

  const lines = tspanSelection.size() || 1
  const height = svgTextSelection.text() ? 0.85 * fontSize * lines * (1 + dy) - dy : 0

  let width = 0
  if (tspanSelection.empty()) {
    const text = svgTextSelection.text()
    width = fastMode ? estimateStringPixelLength(text, fontSize, fontWidthToHeightRatio) : getCachedComputedTextLength(svgTextSelection.node())
  } else {
    for (const tspan of tspanSelection.nodes()) {
      const tspanText = (tspan as SVGTSpanElement).textContent
      const w = fastMode ? estimateStringPixelLength(tspanText, fontSize, fontWidthToHeightRatio) : getCachedComputedTextLength(tspan as SVGTSpanElement)
      if (w > width) width = w
    }
  }

  return { width, height }
}

/**
 * Breaks a text block into lines based on the specified width.
 *
 * @param {UnovisText} textBlock - The text block to break into lines.
 * @param {number | undefined} [width=undefined] - The maximum width of a line in pixels.
 * @param {(number | undefined)} [height=undefined] - The height limit for the wrapped text in pixels.
 * @param {boolean} [fastMode=true] - Whether to use a fast estimation method or a more accurate one.
 * @param {string | string[]} [separator] - The word separators.
 * @returns {string[]} - The text split into lines.
 */
function breakTextIntoLines (
  textBlock: UnovisText,
  width: number | undefined = undefined,
  fastMode = true,
  separator: string | string[] = UNOVIS_TEXT_SEPARATOR_DEFAULT,
  wordBreak = false
): string[] {
  const text = `${textBlock.text}`
  if (!text) return []
  const fontSize = textBlock.fontSize ?? UNOVIS_TEXT_DEFAULT.fontSize
  const fontFamily = textBlock.fontFamily ?? UNOVIS_TEXT_DEFAULT.fontFamily
  const fontWeight = textBlock.fontWeight
  const fontWidthToHeightRatio: number | undefined = textBlock.fontWidthToHeightRatio ?? UNOVIS_TEXT_DEFAULT.fontWidthToHeightRatio
  const separators = Array.isArray(separator) ? separator : [separator]

  const splitByNewLine = text.split('\n')
  return splitByNewLine.map((str) => {
    const lines: string[] = []
    if (!width) return [str]

    const words = splitString(str, separators)
    let line = ''
    for (let i = 0; i < words.length; i += 1) {
      const textLengthPx = fastMode
        ? estimateStringPixelLength(line + words[i], fontSize, fontWidthToHeightRatio)
        : getPreciseStringLengthPx(line + words[i], fontFamily, fontSize, fontWeight)

      if (textLengthPx < width || i === 0) {
        line += words[i]
      } else {
        lines.push(line.trim())
        line = words[i]
      }

      // Word break functionality
      const minCharactersOnLine = 2
      if (wordBreak) {
        while (line.trim().length > minCharactersOnLine) {
          const subLineLengthPx = fastMode
            ? estimateStringPixelLength(line, fontSize, fontWidthToHeightRatio)
            : getPreciseStringLengthPx(line, fontFamily, fontSize, fontWeight)

          if (subLineLengthPx > width) {
            let breakIndex = (line.trim()).length - minCharactersOnLine // Place at least `minCharactersOnLine` characters onto the next line
            while (breakIndex > 0) {
              const subLine = `${line.substring(0, breakIndex)}${UNOVIS_TEXT_HYPHEN_CHARACTER_DEFAULT}` // Use hyphen when force breaking words
              const subLinePx = fastMode
                ? estimateStringPixelLength(subLine, fontSize, fontWidthToHeightRatio)
                : getPreciseStringLengthPx(subLine, fontFamily, fontSize, fontWeight)

              // If the subline is less than the width, or just one character left, break the line
              if (subLinePx <= width || breakIndex === 1) {
                lines.push(subLine.trim())
                line = line.substring(breakIndex)
                break
              }
              breakIndex--
            }
          } else {
            break
          }
        }
      }
    }

    // Adding the final line after the loop
    if (line) lines.push(line.trim())

    return lines
  }).flat()
}

/**
 * Wraps a text or array of texts to fit within specified width and height, if provided.
 *
 * @export
 * @param {UnovisText | UnovisText[]} text - The text or array of texts to wrap.
 * @param {number | undefined} [width=undefined] - The maximum width of a line in pixels.
 * @param {boolean} [fastMode=true] - Whether to use a fast estimation method or a more accurate one.
 * @param {string | string[]} [separator] - The word separators.
 * @returns {UnovisWrappedText[]} - The wrapped texts.
 */
export function getWrappedText (
  text: UnovisText | UnovisText[],
  width: number | undefined = undefined,
  height: number | undefined = undefined,
  fastMode = true,
  separator: string | string[] = UNOVIS_TEXT_SEPARATOR_DEFAULT,
  wordBreak = false
): UnovisWrappedText[] {
  // Merge input text with default values and convert it to an array if it's not already
  const textArrays = Array.isArray(text) ? text.map(t => merge(UNOVIS_TEXT_DEFAULT, t)) : [merge(UNOVIS_TEXT_DEFAULT, text)]

  // Break input text into lines based on width and separator.
  // Per-block `width`, `separator` and `wordBreak` values override the ones provided as arguments.
  const textWrapped: Array<string[]> = textArrays.map(block =>
    breakTextIntoLines(block, block.width ?? width, fastMode, block.separator ?? separator, block.wordBreak ?? wordBreak)
  )

  let h = 0
  let prevBlock: UnovisWrappedText | undefined // The previous block with rendered lines
  const blocks: UnovisWrappedText[] = []

  // Process each text block and its lines based on height limit
  textArrays.forEach((text, i) => {
    let lines = textWrapped[i]

    const blockStartHeight = h
    const dh = text.fontSize * text.lineHeight
    let maxWidth = 0
    const measure = (line: string): number => fastMode
      ? estimateStringPixelLength(line, text.fontSize, text.fontWidthToHeightRatio)
      : getPreciseStringLengthPx(line, text.fontFamily, text.fontSize)

    // Iterate over lines and handle text overflow based on the height limit if provided
    for (let k = 0; k < lines.length; k += 1) {
      let line = lines[k]

      // The very first line adds its own height without the leading; the first line of a subsequent
      // block also clears the previous block's last line and the margin between the blocks; the
      // following lines advance by the block's own line height. Mirrors `renderTextToTspanElements`.
      if (k === 0 && !prevBlock) h += text.marginTop + text.fontSize
      else if (k === 0) h += Math.max(prevBlock.marginBottom, text.marginTop) + prevBlock.fontSize * (prevBlock.lineHeight - 1) + text.fontSize
      else h += dh

      if (height && (h + dh) > height && (k !== lines.length - 1)) {
        // Remove hyphen character from the end of the line if it's there
        const lastCharacter = line.charAt(line.length - 1)
        if (lastCharacter === UNOVIS_TEXT_HYPHEN_CHARACTER_DEFAULT) {
          line = line.substr(0, line.length - 1)
        }

        const lineWithEllipsis = `${line} …`
        if (measure(lineWithEllipsis) < (text.width ?? width)) {
          lines[k] = lineWithEllipsis
        } else {
          lines[k] = `${line.substr(0, line.length - 2)}…`
        }

        maxWidth = Math.max(measure(lines[k]), maxWidth)
        lines = lines.slice(0, k + 1)
        break
      }

      maxWidth = Math.max(measure(line), maxWidth)
    }

    // Create wrapped text block with its calculated properties
    blocks.push({ ...text, _lines: lines, _estimatedHeight: h - blockStartHeight, _maxWidth: maxWidth })
    if (lines.length) prevBlock = blocks[blocks.length - 1]
  })

  return blocks
}


const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'

/**
 * Builds SVG <tspan> elements for the given wrapped text blocks.
 * Constructs the DOM directly (instead of serializing to a string and re-parsing),
 * which avoids the cost of `DOMParser.parseFromString` and string sanitization.
 *
 * @param {UnovisWrappedText[]} blocks - The wrapped text blocks.
 * @param {number} [x=0] - The x-coordinate for the tspan elements.
 * @param {number} [y] - The y-coordinate for the tspan elements.
 * @returns {SVGTSpanElement[]} - One outer <tspan> element per block.
 */
function renderTextToTspanElements (
  blocks: UnovisWrappedText[],
  x = 0,
  y?: number,
  dominantBaseline?: string
): SVGTSpanElement[] {
  let prevBlock: UnovisWrappedText | undefined // The previous block with rendered lines
  return blocks.map((b, i) => {
    // The first line of a block is offset by the margin between the blocks (in pixels, because the
    // blocks can have different font sizes) and by the previous line's height — not this block's
    // one — so that a block following a larger one doesn't overlap it
    const marginPx = Math.max(prevBlock?.marginBottom ?? 0, b.marginTop)
    const firstLineDyPx = prevBlock ? marginPx + prevBlock.fontSize * prevBlock.lineHeight : b.marginTop

    const blockTspan = document.createElementNS(SVG_NAMESPACE, 'tspan')
    if (b.className) blockTspan.setAttribute('class', b.className)
    if (b.fontSize) blockTspan.setAttribute('font-size', `${b.fontSize}`)
    if (b.fontFamily) blockTspan.setAttribute('font-family', `${b.fontFamily}`)
    if (b.fontWeight) blockTspan.setAttribute('font-weight', `${b.fontWeight}`)
    if (b.color) blockTspan.setAttribute('fill', `${b.color}`)
    if (i === 0 && y) blockTspan.setAttribute('y', `${y}`)

    b._lines.forEach((line, k) => {
      const dy = k === 0 ? firstLineDyPx / b.fontSize : b.lineHeight

      const lineTspan = document.createElementNS(SVG_NAMESPACE, 'tspan')
      lineTspan.setAttribute('x', `${x}`)
      lineTspan.setAttribute('dy', `${dy}em`)
      lineTspan.setAttribute('dominant-baseline', dominantBaseline ?? 'auto')
      lineTspan.textContent = line.length ? line : ' '
      blockTspan.appendChild(lineTspan)
    })

    if (b._lines.length) prevBlock = b
    return blockTspan
  })
}

/**
 * Estimates the height of wrapped text blocks.
 *
 * @export
 * @param {UnovisWrappedText[]} blocks - The wrapped text blocks.
 * @returns {number} - The estimated height of the wrapped text blocks.
 */
export function estimateWrappedTextHeight (blocks: UnovisWrappedText[]): number {
  return sum(blocks, b => b._estimatedHeight)
}

/**
 * Estimates the overall bounds of wrapped text blocks without touching the DOM.
 *
 * @export
 * @param {UnovisWrappedText[]} blocks - The wrapped text blocks.
 * @returns {{ width: number; height: number }} - The estimated width and height of the wrapped text blocks.
 */
export function getWrappedTextBounds (blocks: UnovisWrappedText[]): { width: number; height: number } {
  return {
    width: blocks.length ? Math.max(...blocks.map(b => b._maxWidth)) : 0,
    height: estimateWrappedTextHeight(blocks),
  }
}

/**
 * Computes the axis-aligned bounding box of wrapped text blocks as they would be rendered
 * by `renderTextToSvgTextElement`, relative to the text's anchor point.
 *
 * @export
 * @param {UnovisWrappedText[]} blocks - The wrapped text blocks.
 * @param {TextAlign | string} textAlign - The horizontal text alignment.
 * @param {VerticalAlign | string} verticalAlign - The vertical alignment of the text.
 * @param {number} [rotationDeg=0] - Text rotation in degrees, applied around the anchor point.
 * @returns {Rect} - The bounding box, in the coordinate system of the anchor point.
 */
export function getWrappedTextAabb (
  blocks: UnovisWrappedText[],
  textAlign: TextAlign | string,
  verticalAlign: VerticalAlign | string,
  rotationDeg = 0
): Rect {
  const { width, height } = getWrappedTextBounds(blocks)
  const x = textAlign === TextAlign.Center ? -width / 2 : textAlign === TextAlign.Right ? -width : 0
  const y = verticalAlign === VerticalAlign.Middle ? -height / 2 : verticalAlign === VerticalAlign.Bottom ? -height : 0

  const rect: Rect = { x, y, width, height }
  return rotationDeg ? getRotatedRectAabb(rect, rotationDeg * Math.PI / 180) : rect
}

export const allowedSvgTextTags = ['text', 'tspan', 'textPath', 'altGlyph', 'altGlyphDef', 'altGlyphItem', 'glyphRef', 'textRef', 'textArea']

/**
 * Renders a text or array of texts to an SVG text element.
 * Calling this function will replace the contents of the specified SVG text element.
 *
 * @export
 * @param {SVGTextElement} textElement - The SVG text element to render the text into.
 * @param {UnovisText | UnovisText[]} text - The text or array of texts to render.
 * @param {UnovisTextOptions} options - The text options.
 * @returns {UnovisWrappedText[]} - The wrapped text blocks that were rendered.
 */
export function renderTextToSvgTextElement (
  textElement: SVGTextElement,
  text: UnovisText | UnovisText[],
  options: UnovisTextOptions,
  // Dominant baseline sets alignment for a line of text, whereas
  // the `options.verticalAlign` property sets alignment for the entire text block
  // shifting it vertically, irrespective of the dominant baseline.
  dominantBaseline?: string
): UnovisWrappedText[] {
  const wrappedText = getWrappedText(text, options.width, undefined, options.fastMode, options.separator, options.wordBreak)
  const textElementX = options.x ?? +textElement.getAttribute('x')
  const textElementY = options.y ?? +textElement.getAttribute('y')
  const x = textElementX ?? 0
  let y = textElementY ?? 0
  if (options.textAlign) {
    textElement.setAttribute('text-anchor', getTextAnchorFromTextAlign(options.textAlign))
  }

  if (options.verticalAlign && options.verticalAlign !== VerticalAlign.Top) {
    const height = estimateWrappedTextHeight(wrappedText)
    const dy = options.verticalAlign === VerticalAlign.Middle ? -height / 2
      : options.verticalAlign === VerticalAlign.Bottom ? -height : 0

    y += dy
  }

  if (options.textRotationAngle) {
    textElement.setAttribute('transform', `rotate(${(options.textRotationAngle === 0 || options.textRotationAngle) ? options.textRotationAngle : 0} ${x} ${y})`)
  } else {
    textElement.removeAttribute('transform')
  }

  textElement.textContent = ''
  for (const tspan of renderTextToTspanElements(wrappedText, x, y, dominantBaseline)) {
    textElement.appendChild(tspan)
  }

  return wrappedText
}

/**
 * Renders a text or array of texts into a frame.
 * Calling this function will replace the contents of the specified SVG group.
 *
 * @export
 * @param {SVGGElement} group - The SVG group element to render the text into.
 * @param {UnovisText | UnovisText[]} text - The text or array of texts to render.
 * @param {UnovisTextFrameOptions} frameOptions - The text frame options.
 */
export function renderTextIntoFrame (
  group: SVGGElement,
  text: UnovisText | UnovisText[],
  frameOptions: UnovisTextFrameOptions
): void {
  const wrappedText = getWrappedText(text, frameOptions.width, frameOptions.height, frameOptions.fastMode, frameOptions.separator, frameOptions.wordBreak)

  const x = frameOptions.textAlign === TextAlign.Center ? frameOptions.width / 2
    : frameOptions.textAlign === TextAlign.Right ? frameOptions.width : 0

  let y = 0
  const height = estimateWrappedTextHeight(wrappedText)

  // If the frame has height, the text will be vertically aligned within the frame.
  // If not, the text will be aligned against the `y` position of the frame.
  const dh = frameOptions.height - height
  y = frameOptions.verticalAlign === VerticalAlign.Middle ? dh / 2
    : frameOptions.verticalAlign === VerticalAlign.Bottom ? dh : 0


  const textEl = document.createElementNS(SVG_NAMESPACE, 'text')
  textEl.setAttribute('text-anchor', getTextAnchorFromTextAlign(frameOptions.textAlign))
  if (frameOptions.x || frameOptions.y) {
    textEl.setAttribute('transform', `translate(${frameOptions.x ?? 0},${frameOptions.y ?? 0})`)
  }
  for (const tspan of renderTextToTspanElements(wrappedText, x, y, 'hanging')) {
    textEl.appendChild(tspan)
  }

  group.textContent = ''
  group.appendChild(textEl)
}

